import React, { useEffect, useState } from "react";
import { useParams, Link, useLocation, useNavigate } from "react-router-dom";
import { api, imgUrl, formatApiError } from "@/lib/api";
import { vehicleUrl } from "@/lib/urls";
import { useFavorites } from "@/hooks/useFavorites";
import { toast } from "sonner";
import { Calendar, Gauge, Fuel, Settings, Zap, MapPin, ArrowLeft, Phone, Building2, Heart, X, ChevronLeft, ChevronRight, Maximize2 } from "lucide-react";
import ImagePlaceholder from "@/components/ImagePlaceholder";

const fmtPrice = (n) => new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n || 0);
const fmtKm = (n) => new Intl.NumberFormat("de-DE").format(n || 0) + " km";
const DETAIL_LABELS = {
  condition: "Zustand",
  body_type: "Karosserieform",
  first_registration: "Erstzulassung",
  hu_until: "HU bis",
  doors: "Türen",
  seats: "Sitzplätze",
  color: "Außenfarbe",
  metallic: "Metallic",
  interior: "Innenausstattung",
  interior_color: "Innenraumfarbe",
  upholstery: "Polsterung",
  climatisation: "Klimatisierung",
  headlight_type: "Scheinwerfer",
  rim_size: "Felgengröße",
  drive_type: "Antriebsart",
  cubic_capacity: "Hubraum",
  emission_class: "Schadstoffklasse",
  environmental_badge: "Umweltplakette",
  consumption: "Verbrauch kombiniert",
  co2_emissions: "CO₂-Emissionen",
  previous_owners: "Vorbesitzer",
  accident_vehicle: "Unfallfahrzeug",
  damaged_vehicle: "Beschädigtes Fahrzeug",
  service_history: "Scheckheftgepflegt",
  non_smoker: "Nichtraucherfahrzeug",
  vat_deductible: "MwSt. ausweisbar",
  warranty: "Garantie",
  availability: "Verfügbarkeit",
  financing_available: "Finanzierung möglich",
  trade_in: "Inzahlungnahme möglich",
  leasing_takeover: "Leasingübernahme",
  export_available: "Export möglich",
};
const DETAIL_ORDER = [
  "condition", "body_type", "first_registration", "hu_until", "doors", "seats",
  "color", "metallic", "upholstery", "interior_color", "interior", "climatisation",
  "headlight_type", "rim_size", "drive_type", "cubic_capacity", "previous_owners",
  "emission_class", "environmental_badge", "consumption", "co2_emissions",
  "accident_vehicle", "damaged_vehicle", "service_history", "non_smoker", "vat_deductible", "warranty",
  "availability", "financing_available", "trade_in", "leasing_takeover", "export_available",
];
const FEATURE_GROUPS = [
  ["comfort_features", "Komfort"],
  ["safety_features", "Sicherheit & Assistenz"],
  ["multimedia_features", "Multimedia"],
  ["exterior_features", "Exterieur & Fahrwerk"],
  ["e_mobility_features", "E-Mobilität"],
];

export default function CarDetail() {
  const { slug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { isFav, toggle } = useFavorites();
  const [v, setV] = useState(null);
  const [active, setActive] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    const isLegacyId = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(slug || "");
    const endpoint = isLegacyId ? `/vehicles/${slug}` : `/vehicles/slug/${slug}`;
    api.get(endpoint).then(r => {
      setV(r.data);
      setForm(f => ({ ...f, message: `Guten Tag,\n\nich interessiere mich für Ihr Fahrzeug "${r.data.title}".\nIst dieses noch verfügbar?\n\nMit freundlichen Grüßen` }));
      const canonical = vehicleUrl(r.data);
      if (location.pathname !== canonical) {
        navigate(canonical, { replace: true });
      }
    }).catch(() => setV(false));
  }, [slug, location.pathname, navigate]);

  useEffect(() => {
    if (!lightboxOpen) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") setLightboxOpen(false);
      if (e.key === "ArrowLeft") setActive(i => (i - 1 + (v?.images?.length || 1)) % (v?.images?.length || 1));
      if (e.key === "ArrowRight") setActive(i => (i + 1) % (v?.images?.length || 1));
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [lightboxOpen, v?.images?.length]);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post(`/vehicles/${v.id}/inquiries`, form);
      setSent(true);
      toast.success("Anfrage gesendet!");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Fehler beim Senden");
    } finally {
      setSending(false);
    }
  };

  if (v === false) return <div className="page-wrap py-20 text-center text-gray-500" data-testid="vehicle-not-found">Fahrzeug nicht gefunden.</div>;
  if (!v) return <div className="page-wrap py-20 text-center text-gray-500">Lade…</div>;

  const images = v.images?.length ? v.images : [];
  const showPrevImage = () => setActive(i => (i - 1 + images.length) % images.length);
  const showNextImage = () => setActive(i => (i + 1) % images.length);
  const detailItems = DETAIL_ORDER
    .filter(key => v.details?.[key])
    .map(key => ({ label: DETAIL_LABELS[key], value: formatDetailValue(key, v.details[key]) }));
  const featureGroups = FEATURE_GROUPS
    .map(([key, title]) => ({ key, title, items: v.details?.[key] || [] }))
    .filter(group => group.items.length > 0);

  return (
    <div className="page-wrap py-8">
      <Link to="/fahrzeuge" className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-[#16A34A] mb-6">
        <ArrowLeft className="w-4 h-4" /> Zurück zur Übersicht
      </Link>

      <div className="grid lg:grid-cols-[minmax(0,1fr)_380px] xl:grid-cols-[minmax(0,1fr)_400px] gap-6 lg:gap-10">
        {/* Left — gallery + details */}
        <div className="min-w-0">
          <div className="aspect-[16/10] bg-gray-100 overflow-hidden relative">
            {images[active] ? (
              <button
                type="button"
                onClick={() => setLightboxOpen(true)}
                className="block w-full h-full group"
                aria-label="Bild vergrößern"
              >
                <img src={imgUrl(images[active])} alt={v.title} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.02]" data-testid="vehicle-main-image" />
                <span className="absolute left-3 bottom-3 md:left-4 md:bottom-4 inline-flex items-center gap-2 h-9 px-3 bg-white/95 text-gray-900 text-xs font-semibold opacity-0 group-hover:opacity-100 transition-opacity">
                  <Maximize2 className="w-4 h-4" />
                  Vergrößern
                </span>
              </button>
            ) : (
              <ImagePlaceholder />
            )}
            <button
              onClick={() => toggle(v.id)}
              data-testid="detail-fav-btn"
              aria-label={isFav(v.id) ? "Aus Merkliste entfernen" : "Zur Merkliste hinzufügen"}
              className={`absolute top-3 right-3 md:top-4 md:right-4 inline-flex items-center gap-1.5 px-2.5 h-9 md:px-4 md:h-11 text-xs md:text-sm font-semibold transition-all ${isFav(v.id) ? "bg-[#16A34A] text-white" : "bg-white/95 text-gray-800 hover:bg-white"}`}
            >
              <Heart className="w-4 h-4" fill={isFav(v.id) ? "currentColor" : "none"} />
              <span className="hidden sm:inline">{isFav(v.id) ? "Gemerkt" : "Merken"}</span>
            </button>
          </div>
          {images.length > 1 && (
            <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 gap-2 mt-2">
              {images.map((p, i) => (
                <button key={i} onClick={() => setActive(i)} className={`aspect-[4/3] overflow-hidden border-2 ${active === i ? "border-[#16A34A]" : "border-transparent"}`}>
                  <img src={imgUrl(p)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}

          <div className="mt-8 md:mt-10">
            <div className="swiss-label text-[#16A34A]">{v.brand} · {v.year}</div>
            <h1 className="font-display text-2xl md:text-3xl lg:text-4xl font-bold tracking-tight mt-2 break-words">{v.title}</h1>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 sm:gap-x-6 md:gap-x-8 gap-y-5 mt-6 md:mt-8 swiss-card p-5 md:p-6">
              <Spec icon={Calendar} label="Erstzulassung" val={v.year} />
              <Spec icon={Gauge} label="Kilometerstand" val={fmtKm(v.mileage)} />
              <Spec icon={Fuel} label="Kraftstoff" val={v.fuel} />
              <Spec icon={Settings} label="Getriebe" val={v.transmission} />
              <Spec icon={Zap} label="Leistung" val={`${v.power_hp} PS`} />
              <Spec icon={MapPin} label="Standort" val={v.location || "—"} />
            </div>

            {detailItems.length > 0 && (
              <div className="mt-8 md:mt-10">
                <h2 className="font-display text-xl md:text-2xl font-bold tracking-tight">Fahrzeugdetails</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 mt-3 md:mt-4 swiss-card p-5 md:p-6">
                  {detailItems.map(item => (
                    <div key={item.label} className="flex items-start justify-between gap-4 border-b border-gray-100 pb-2 last:border-b-0">
                      <span className="text-sm text-gray-500">{item.label}</span>
                      <span className="text-sm font-semibold text-right text-gray-800">{item.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {featureGroups.length > 0 && (
              <div className="mt-8 md:mt-10">
                <h2 className="font-display text-xl md:text-2xl font-bold tracking-tight">Ausstattung & Extras</h2>
                <div className="space-y-4 mt-3 md:mt-4">
                  {featureGroups.map(group => (
                    <div key={group.key} className="swiss-card p-5 md:p-6">
                      <h3 className="swiss-label text-gray-500 mb-3">{group.title}</h3>
                      <div className="flex flex-wrap gap-2">
                        {group.items.map(item => (
                          <span key={item} className="text-sm font-medium bg-gray-50 border border-gray-200 px-3 py-1.5">{item}</span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-8 md:mt-10">
              <h2 className="font-display text-xl md:text-2xl font-bold tracking-tight">Beschreibung</h2>
              <div className="prose prose-sm max-w-none mt-3 md:mt-4 text-gray-700 whitespace-pre-wrap leading-relaxed break-words">{v.description}</div>
            </div>
          </div>
        </div>

        {/* Right — sticky sidebar */}
        <aside>
          <div className="md:sticky md:top-6">
            <div className="swiss-card p-5 md:p-6">
              <div className="text-3xl md:text-4xl font-display font-extrabold break-words">{fmtPrice(v.price)}</div>
              <p className="text-xs text-gray-500 mt-1">inkl. MwSt. ausweisbar (sofern zutreffend)</p>
            </div>

            {v.dealer && v.dealer.role === "dealer" ? (
              <Link to={`/haendler/${v.dealer.id}`} data-testid="vehicle-dealer-link" className="swiss-card p-6 mt-3 block hover:border-[#16A34A] transition-colors group">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="swiss-label text-gray-500">Händler</div>
                    <div className="font-semibold text-base mt-2 flex items-center gap-2"><Building2 className="w-4 h-4 text-[#16A34A]" /> {v.dealer.company || v.dealer.name}</div>
                    {v.dealer.phone && <div className="text-sm text-gray-600 mt-1 flex items-center gap-2"><Phone className="w-4 h-4" /> {v.dealer.phone}</div>}
                    <div className="text-sm text-gray-500 mt-1">{v.location}</div>
                  </div>
                  <span className="swiss-label text-gray-400 group-hover:text-[#16A34A] transition-colors">Profil →</span>
                </div>
              </Link>
            ) : v.dealer ? (
              <div className="swiss-card p-6 mt-3" data-testid="vehicle-seller-card">
                <div className="swiss-label text-gray-500">Privatverkäufer</div>
                <div className="font-semibold text-base mt-2 flex items-center gap-2"><Building2 className="w-4 h-4 text-[#16A34A]" /> {v.dealer.name}</div>
                {v.dealer.phone && <div className="text-sm text-gray-600 mt-1 flex items-center gap-2"><Phone className="w-4 h-4" /> {v.dealer.phone}</div>}
                <div className="text-sm text-gray-500 mt-1">{v.location}</div>
              </div>
            ) : null}

            <div className="swiss-card p-6 mt-3">
              <h3 className="font-display font-bold text-lg">Anfrage senden</h3>
              <p className="text-sm text-gray-500 mb-5">Direkt an den Händler — kostenfrei.</p>
              {sent ? (
                <div data-testid="inquiry-success" className="bg-green-50 border border-green-200 p-4 text-sm text-green-800">
                  Vielen Dank! Deine Anfrage wurde gesendet.
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-3">
                  <input required data-testid="inquiry-name" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} placeholder="Dein Name *" className="w-full border border-gray-200 py-2.5 px-3 text-sm focus:border-[#16A34A] outline-none" />
                  <input required type="email" data-testid="inquiry-email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} placeholder="E-Mail *" className="w-full border border-gray-200 py-2.5 px-3 text-sm focus:border-[#16A34A] outline-none" />
                  <input data-testid="inquiry-phone" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} placeholder="Telefon (optional)" className="w-full border border-gray-200 py-2.5 px-3 text-sm focus:border-[#16A34A] outline-none" />
                  <textarea required data-testid="inquiry-message" value={form.message} onChange={(e) => setForm({...form, message: e.target.value})} rows={5} className="w-full border border-gray-200 py-2.5 px-3 text-sm focus:border-[#16A34A] outline-none" />
                  <button data-testid="inquiry-submit" disabled={sending} className="swiss-btn-primary w-full">{sending ? "Senden…" : "Anfrage abschicken"}</button>
                </form>
              )}
            </div>
          </div>
        </aside>
      </div>

      {lightboxOpen && images[active] && (
        <div
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
          role="dialog"
          aria-modal="true"
          aria-label="Fahrzeugbilder"
          onClick={() => setLightboxOpen(false)}
        >
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setLightboxOpen(false);
            }}
            className="absolute top-3 right-3 md:top-5 md:right-5 w-11 h-11 inline-flex items-center justify-center bg-white/10 text-white hover:bg-white/20 transition-colors"
            aria-label="Lightbox schließen"
          >
            <X className="w-6 h-6" />
          </button>

          {images.length > 1 && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                showPrevImage();
              }}
              className="absolute left-3 md:left-5 top-1/2 -translate-y-1/2 w-11 h-11 md:w-12 md:h-12 inline-flex items-center justify-center bg-white/10 text-white hover:bg-white/20 transition-colors"
              aria-label="Vorheriges Bild"
            >
              <ChevronLeft className="w-7 h-7" />
            </button>
          )}

          <div className="w-full h-full px-4 py-16 md:px-20 md:py-14 flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
            <img
              src={imgUrl(images[active])}
              alt={v.title}
              className="max-w-full max-h-full object-contain"
              data-testid="vehicle-lightbox-image"
            />
          </div>

          {images.length > 1 && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                showNextImage();
              }}
              className="absolute right-3 md:right-5 top-1/2 -translate-y-1/2 w-11 h-11 md:w-12 md:h-12 inline-flex items-center justify-center bg-white/10 text-white hover:bg-white/20 transition-colors"
              aria-label="Nächstes Bild"
            >
              <ChevronRight className="w-7 h-7" />
            </button>
          )}

          {images.length > 1 && (
            <div className="absolute left-1/2 -translate-x-1/2 bottom-4 md:bottom-5 text-white/80 text-sm font-semibold">
              {active + 1} / {images.length}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Spec({ icon: Icon, label, val }) {
  return (
    <div>
      <div className="swiss-label text-gray-400 flex items-center gap-1.5 mb-1"><Icon className="w-3.5 h-3.5" /> {label}</div>
      <div className="font-semibold text-sm">{val}</div>
    </div>
  );
}

function formatDetailValue(key, value) {
  if (key === "first_registration" || key === "hu_until") {
    const [year, month] = String(value).split("-");
    if (year && month) return `${month}/${year}`;
  }
  if (key === "cubic_capacity") return `${new Intl.NumberFormat("de-DE").format(value)} cm³`;
  if (key === "co2_emissions") return `${value} g/km`;
  return value;
}
